import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Toast from '../components/Toast';

const ToastContext = createContext();

/**
 * Hook to access toast functions
 * @returns {Object} { showToast, dismissToast }
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * Toast Provider Component
 * Manages toast notifications with auto-dismiss and stacking
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const toastTimers = useRef({});
  const nextId = useRef(0);

  /**
    * Dismiss a toast by ID
    * @param {number} id - Toast ID to dismiss
    */
   const dismissToast = useCallback((id) => {
     setToasts(prev => prev.filter(toast => toast.id !== id));

     // Clear timer if exists
     if (toastTimers.current[id]) {
       clearTimeout(toastTimers.current[id]);
       delete toastTimers.current[id];
     }
   }, []);

   /**
    * Show a toast notification
    * @param {string} message - Toast message
    * @param {string} type - Toast type (success, error, warning, info)
    * @param {number} duration - Duration in ms (0 = no auto-dismiss)
    * @param {Object} action - Optional action button { label, onClick }
    */
   const showToast = useCallback((message, type = 'info', duration, action) => {
     const id = nextId.current++;

     // Default durations by type
     const defaultDurations = {
       success: 3000,
       error: 5000,
       warning: 4000,
       info: 3000
     };

     const toastDuration = duration !== undefined ? duration : defaultDurations[type];

     const newToast = {
       id,
       message,
       type,
       duration: toastDuration,
       action,
       isPaused: false
     };

     setToasts(prev => {
       // Limit to 3 visible toasts
       const updated = [...prev, newToast];
       if (updated.length > 3) {
         // Remove oldest toast timers
         const removed = updated.shift();
         if (toastTimers.current[removed.id]) {
           clearTimeout(toastTimers.current[removed.id]);
           delete toastTimers.current[removed.id];
         }
       }
       return updated;
     });

     // Set auto-dismiss timer if duration > 0
     if (toastDuration > 0) {
       toastTimers.current[id] = setTimeout(() => {
         dismissToast(id);
       }, toastDuration);
     }

     return id;
    }, [dismissToast]);

  /**
   * Pause auto-dismiss timer (on hover)
   * @param {number} id - Toast ID
   */
  const pauseToast = useCallback((id) => {
    if (toastTimers.current[id]) {
      clearTimeout(toastTimers.current[id]);
    }
  }, []);

  /**
   * Resume auto-dismiss timer (on mouse leave)
   * @param {number} id - Toast ID
   * @param {number} remainingTime - Remaining time in ms
   */
  const resumeToast = useCallback((id, remainingTime) => {
    if (remainingTime > 0) {
      toastTimers.current[id] = setTimeout(() => {
        dismissToast(id);
      }, remainingTime);
    }
  }, [dismissToast]); // dismissToast is stable, but we include it for completeness

  const value = {
    showToast,
    dismissToast,
    pauseToast,
    resumeToast
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[1200] flex flex-col gap-2 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <Toast
              id={toast.id}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              action={toast.action}
              onClose={() => dismissToast(toast.id)}
              onPause={() => pauseToast(toast.id)}
              onResume={(remaining) => resumeToast(toast.id, remaining)}
            />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
